import { ChangeEvent, useEffect, useState } from 'react';
import values from 'lodash/values';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { USERS_ORDER_OPTIONS, USER_ROLE } from '../lib/constants';
import { cln } from '../lib/utils';
import { useUsersQuery } from '../lib/queries';
import { useDebounce } from '../hooks';
import { PATHS } from '../lib/paths';
import { usersPaginationAtom } from '../atoms/usersPaginationAtom';
import { UserDetails } from '../components/UserDetails';
import { Input } from '../components/Input';
import { Select } from '../components/Select';

export default function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useAtom(usersPaginationAtom);
  const usersQuery = useUsersQuery(pagination);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 700);

  const roleFilter = pagination.filters?.role?.$in;

  const setUsersPaginationRoleFilter = (value: string[]) => {
    setPagination({
      ...pagination,
      filters: {
        ...pagination.filters,
        role: {
          $in: value,
        },
      },
    });
  };

  const handleFilterClick = (filter: string) => () => {
    const relevantRole = roleFilter?.some((role) => role === filter);

    if (relevantRole) setUsersPaginationRoleFilter(roleFilter?.filter((f) => f !== filter) || []);
    else setUsersPaginationRoleFilter([...(roleFilter || []), filter]);
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        search: debouncedSearch,
      },
    }));
  }, [debouncedSearch, setPagination]);

  const orderDefaultValue = USERS_ORDER_OPTIONS.findIndex(
    (option) => option.direction === pagination.sort?.createdAt,
  );

  return (
    <main className="grid gap-2 w-700 mx-auto">
      <div className="flex items-center">
        <h1 className="font-bold text-hero grow">{t('users.title')}</h1>
        <button className="primary-fill !pl-2" onClick={() => navigate(PATHS.newUserPath())}>
          <i className="ri-add-line" />
          {t('users.new')}
        </button>
      </div>

      <div className="flex gap-4 items-end w-full mt-10 mb-5">
        <Input
          label="common.search"
          placeholder="search"
          className="grow"
          type="search"
          value={search}
          onInput={(e: ChangeEvent<HTMLInputElement>) => {
            setSearch(e.target.value);
          }}
        />
      </div>
      <div className="flex mb-5">
        <div className="flex items-end gap-2">
          {values(USER_ROLE).map((role) => (
            <button
              key={role}
              className={cln(
                'chip',
                roleFilter?.includes(role) && 'primary-fill border-transparent',
              )}
              onClick={handleFilterClick(role)}
            >
              {t(`user.${role}`)}
            </button>
          ))}
        </div>
        <Select
          label="order.label"
          className="justify-end"
          defaultValue={orderDefaultValue}
          onChange={(e: any) => {
            const { direction, field } = USERS_ORDER_OPTIONS[e.target.value];
            setPagination((p) => ({ ...p, sort: { ...p.sort, [field]: direction } }));
          }}
        >
          {USERS_ORDER_OPTIONS.map((opt, index) => (
            <option key={opt.label} value={index}>
              {t(opt.label)}
            </option>
          ))}
        </Select>
      </div>
      {usersQuery.data?.users.map((user) => <UserDetails key={user.id} user={user} />)}
    </main>
  );
}
